import { Text } from '@cdl/heartbeat-react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  gainSelector,
  showDelineationSelector,
} from '../layout/selectors/layout.selectors';
import { DefaultLeads } from '../plot/components/DefaultLeads';
import { EditableStrip } from '../plot/components/EditableStrip';
import { Grid } from '../plot/components/Grid';
import { QrsWaves } from '../plot/components/QrsWaves';
import { StripContent } from '../plot/components/StripContent';
import { StripPosition } from '../plot/components/StripPosition';
import { Row } from '../plot/contexts/row.context';
import { Strip } from '../plot/contexts/strip.context';
import { parseLeadName } from '../plot/utils/lead';
import {
  RESTING_LEAD_TEXT_OFFSET,
  RESTING_STRIP_HEIGHT,
  RESTING_STRIP_WIDTH,
  RESTING_DURATION_ON_SCREEN,
  RESTING_ONSET_ON_SCREEN,
  RESTING_OFFSET_ON_SCREEN,
} from './resting.util';
import * as stripActions from '../plot/actions/strip.actions';
import { QRSCount } from '../plot/components/QrsCount';

interface Props {
  leadChunkIndex: number;
  lead: string;
  duration: number;
  count: number;
}

export const RestingLead: React.FC<Props> = ({
  lead,
  duration,
  leadChunkIndex,
  count,
}) => {
  const dispatch = useDispatch();
  const showDelineation = useSelector(showDelineationSelector);
  const gain = useSelector(gainSelector);
  const isLastLead = count === 1 && leadChunkIndex === 0;

  useEffect(() => {
    dispatch(stripActions.update({ stripId: lead, strip: { gain } }));
  }, [gain, dispatch, lead]);

  return (
    <Strip
      id={lead}
      duration={RESTING_DURATION_ON_SCREEN}
      leads={[lead]}
      onset={RESTING_ONSET_ON_SCREEN}
      offset={RESTING_OFFSET_ON_SCREEN}
    >
      <svg width={RESTING_STRIP_WIDTH / count}>
        <g
          transform={`translate(${
            (-RESTING_STRIP_WIDTH / count) * leadChunkIndex
          })`}
        >
          <Row height={RESTING_STRIP_HEIGHT}>
            <g
              transform={`translate(
                ${(RESTING_STRIP_WIDTH / count) * leadChunkIndex}
                ${RESTING_STRIP_HEIGHT / 2 - RESTING_LEAD_TEXT_OFFSET}
              )`}
              // RESTING_STRIP_HEIGHT / 2 - RESTING_LEAD_TEXT_OFFSET:
              // 20px higher so that it doesn't overlap with the signal
            >
              <Text
                as='text'
                fill='neutral.400'
                fontSize='xxl'
                cursor='default'
                userSelect='none'
              >
                {parseLeadName(lead)}
              </Text>
            </g>
            <EditableStrip width={RESTING_STRIP_WIDTH}>
              <StripPosition>
                <StripContent clipType='plot'>
                  <Grid />
                </StripContent>
                {showDelineation && <QrsWaves />}
                <DefaultLeads />
                {isLastLead && <QRSCount />}
              </StripPosition>
            </EditableStrip>
          </Row>
        </g>
      </svg>
    </Strip>
  );
};
